"use client";

import { useState, useRef } from "react";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import Image from "next/image";

interface ImageUploadProps {
    value?: string | null;
    onChange: (url: string | null) => void;
    label?: string;
}

export default function ImageUpload({ value, onChange, label = "Dodaj zdjęcie" }: ImageUploadProps) {
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Basic validation
        if (!file.type.startsWith("image/")) {
            alert("Proszę wybrać plik graficzny (JPG, PNG, WEBP)");
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            alert("Plik jest zbyt duży (max 5MB)");
            return;
        }

        setIsUploading(true);

        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await fetch(`/api/upload?filename=${encodeURIComponent(file.name)}`, {
                method: "POST",
                body: file, // Send binary directly or use formData. generic api expects binary body or formData? 
                // My generic api implemented generic request.body put. 
                // Actually, put(filename, body) works with file object directly from client usually if supported, 
                // but sending as binary body in fetch is easiest for valid 'request.body' stream on server.
                headers: {
                    // No Content-Type header so browser sets boundary? 
                    // Wait, if sending raw binary, browser sets it?
                    // Let's stick to raw body upload for simplicity with my implementation.
                },
            });

            if (!response.ok) {
                throw new Error("Upload failed");
            }

            const blob = await response.json();
            onChange(blob.url);
        } catch (error) {
            console.error(error);
            alert("Wystąpił błąd podczas przesyłania zdjęcia");
        } finally {
            setIsUploading(false);
            // Reset input so same file can be selected again if needed
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleRemove = () => {
        onChange(null);
    };

    return (
        <div className="space-y-2">
            <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <ImageIcon size={16} />
                {label} (Opcjonalnie)
            </span>

            {value ? (
                <div className="relative w-full h-48 md:h-64 bg-gray-900 rounded-lg overflow-hidden border border-gray-800 group">
                    <Image
                        src={value}
                        alt="Uploaded image"
                        fill
                        className="object-contain"
                    />
                    <button
                        onClick={handleRemove}
                        type="button"
                        className="absolute top-2 right-2 bg-black/50 hover:bg-red-500/80 text-white p-2 rounded-full transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>
            ) : (
                <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`
                        border-2 border-dashed border-gray-700 rounded-lg p-6
                        flex flex-col items-center justify-center gap-2 cursor-pointer
                        hover:border-primary-500 hover:bg-gray-800/50 transition-all
                        ${isUploading ? "opacity-50 pointer-events-none" : ""}
                    `}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                    />
                    {isUploading ? (
                        <Loader2 className="animate-spin text-gray-400" size={24} />
                    ) : (
                        <Upload className="text-gray-400" size={24} />
                    )}
                    <span className="text-sm text-gray-400">
                        {isUploading ? "Przesyłanie..." : "Kliknij, aby dodać zdjęcie"}
                    </span>
                    <span className="text-xs text-gray-600">Max 5MB (JPG, PNG)</span>
                </div>
            )}
        </div>
    );
}
